'use client';

import Image from 'next/image';
import type { ConfigurableOptionValue } from '@cobrastyle/shared-types';
import { cn } from '@cobrastyle/ui';
import type { SwatchSize } from '@/lib/configurable-options';

interface OptionButtonProps {
  value: ConfigurableOptionValue;
  isSelected: boolean;
  onClick: () => void;
  /** How large to draw a visual swatch — see `swatchSize` in lib. */
  size?: SwatchSize;
}

/**
 * Box for an image swatch.
 *
 * `md` is a fixed chip, sized to sit in a wrapping row beside its siblings.
 *
 * `lg` fills its grid cell instead of taking a fixed width — the caller lays
 * these out four to a row across the buy box (see the option grid in
 * product-details.tsx), so the tile has to stretch to whatever a quarter of
 * that column is. `aspect-square` keeps it square as it stretches.
 *
 * The fit differs too, because the two carry different kinds of picture. A
 * fabric photograph is a texture, so cropping it to fill the square loses
 * nothing. A reticle is a line drawing centred in its frame, and cropping one
 * cuts the crosshair off — so `lg` contains rather than covers.
 */
const IMAGE_SWATCH_BOX: Record<SwatchSize, { box: string; sizes: string; fit: string }> = {
  md: { box: 'h-12 w-12', sizes: '48px', fit: 'object-cover' },
  lg: {
    box: 'aspect-square w-full',
    // A quarter of the buy box on desktop, a quarter of the viewport below it.
    sizes: '(min-width: 1024px) 130px, 25vw',
    fit: 'object-contain p-1.5',
  },
};

/**
 * One choice in a configurable option — a colour chip, a picture, or a word.
 *
 * Which of the three depends on the swatch Magento attaches to the option:
 *
 * - `color` — a hex fill, so the customer sees the colour itself.
 * - `image` — a photograph of the option. Torsbo uses this for `color` (a shot
 *   of the fabric) and, per p.10 of the "Hemsidan 2.0" feedback, for
 *   `th_riktmedel`, where the reticle names alone are "lite förvirrande för
 *   kunderna" and the old site drew the picture beside them.
 * - `text`, or no swatch at all — the label as a button. Magento's text-swatch
 *   value is normally the label repeated ("EU36"/"EU36"), so drawing the label
 *   is both simpler and what the size axis wants.
 *
 * A visual swatch keeps the label as its accessible name rather than dropping
 * it: "Off white" has to reach a screen reader, and a hex fill or a 40px
 * photograph tells it nothing.
 */
export function OptionButton({ value, isSelected, onClick, size = 'md' }: OptionButtonProps) {
  const swatch = value.swatch;
  const hasColor = swatch?.type === 'color' && Boolean(swatch.value);
  const hasImage = swatch?.type === 'image' && Boolean(swatch.value);

  if (hasImage) {
    return (
      <button
        onClick={onClick}
        // Square, and larger than the colour chip: these are pictures rather
        // than fills — see IMAGE_SWATCH_BOX for why the two sizes exist.
        className={cn(
          'relative isolate overflow-hidden rounded-lg border transition-all duration-200',
          IMAGE_SWATCH_BOX[size].box,
          isSelected
            ? 'border-primary-500 ring-2 ring-primary-500/30'
            : 'border-border hover:border-border-hover',
        )}
        title={value.label}
        aria-label={value.label}
        aria-pressed={isSelected}
      >
        <Image
          src={swatch!.value}
          alt=""
          fill
          className={IMAGE_SWATCH_BOX[size].fit}
          sizes={IMAGE_SWATCH_BOX[size].sizes}
          // Magento's swatch files are ~1.5 kB and already swatch-sized, so
          // there is nothing to gain from lazy-loading a handful of them, and
          // the buy box is above the fold.
          unoptimized
        />
      </button>
    );
  }

  if (hasColor) {
    return (
      <button
        onClick={onClick}
        className={cn(
          'h-9 w-9 rounded-lg border transition-all duration-200',
          isSelected
            ? 'border-primary-500 ring-2 ring-primary-500/30 scale-105'
            : 'border-border hover:border-border-hover hover:scale-105',
        )}
        style={{ backgroundColor: swatch!.value }}
        title={value.label}
        aria-label={value.label}
        aria-pressed={isSelected}
      />
    );
  }

  return (
    <button
      onClick={onClick}
      className={cn(
        'min-w-12 rounded-lg border px-3 py-2 text-xs font-medium text-foreground transition-all duration-200',
        isSelected
          ? 'border-primary-500 bg-primary-500 text-white'
          : 'border-border bg-card hover:border-border-hover hover:bg-card-hover',
      )}
      aria-pressed={isSelected}
    >
      {value.label}
    </button>
  );
}
